import { ChevronRight, Loader2, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { useFetcher } from "~/hooks/useFetcher";
import { ResizableSidePanel } from "~/pages/manifold-nextgen/components/ResizableSidePanel";
import { ArtifactLineagePanel } from "~/pages/manifold-nextgen/components/conversations/artifacts/lineage/ArtifactLineagePanel";
import { recipeErrorMessage } from "~/pages/manifold-nextgen/components/conversations/artifacts/lineage/recipeErrorMessage";
import { useArtifactPreview } from "~/pages/manifold-nextgen/components/conversations/artifacts/useArtifactPreview";
import { artifactLabel } from "~/pages/manifold-nextgen/components/projects/artifactLabel";
import { sharedArtifactRecipePath, type ArtifactPreview } from "~/services/artifacts";
import type { WorkflowSpec } from "~/services/pinnedArtifacts";

interface SharedArtifactLineagePanelProps {
  shareToken: string;
  artifactName: string;
  onClose: () => void;
}

/** One artifact's lineage beside a shared session, read through its share
 *  token. Viewers are read-only, so it offers no sharing or download. */
export const SharedArtifactLineagePanel = ({
  shareToken,
  artifactName,
  onClose,
}: SharedArtifactLineagePanelProps) => {
  const { preview, error, isLoading, notFound } = useArtifactPreview(
    { conversationId: null, shareToken },
    artifactName
  );

  return (
    <ResizableSidePanel role="complementary" aria-label="Artifact lineage">
      <div className="flex h-14 shrink-0 items-center gap-1 border-b border-border px-2">
        <span className="shrink-0 pl-1.5 text-sm font-medium text-muted-foreground">Artifacts</span>
        <ChevronRight className="size-4 shrink-0 text-muted-foreground/50" aria-hidden />
        <span className="min-w-0 truncate text-sm font-medium">
          {artifactLabel(artifactName, preview, null)}
        </span>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Close lineage"
          className="ml-auto size-8 shrink-0"
        >
          <X className="size-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2
              className="size-5 animate-spin text-muted-foreground"
              aria-label="Loading artifact"
            />
          </div>
        ) : error || notFound || !preview ? (
          <p className="p-4 text-xs text-destructive">
            {notFound
              ? "This artifact is not in the shared session."
              : "Couldn't load this artifact."}
          </p>
        ) : (
          <SharedArtifactLineage key={artifactName} shareToken={shareToken} preview={preview} />
        )}
      </div>
    </ResizableSidePanel>
  );
};

const SharedArtifactLineage = ({
  shareToken,
  preview,
}: {
  shareToken: string;
  preview: ArtifactPreview;
}) => {
  const label = artifactLabel(preview.artifact_name ?? preview.artifact_id, preview, null);
  const {
    data: recipe,
    error,
    isLoading,
  } = useFetcher<WorkflowSpec>(sharedArtifactRecipePath(shareToken, preview.artifact_id));

  return (
    <ArtifactLineagePanel
      name={label}
      // The label is usually the summary; don't print it twice.
      summary={preview.summary === label ? null : preview.summary}
      artifactType={preview.artifact_type}
      preview={preview}
      recipe={recipe}
      isLoading={isLoading}
      recipeError={error ? recipeErrorMessage(error) : null}
    />
  );
};
